/**
 * Marketing → Templates sub-page. Reusable HTML blocks the broadcast composer
 * can pre-fill. Simple CRUD.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Copy, Edit } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function MarketingTemplates() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/marketing/templates");
    setRows(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete template?")) return;
    await api.delete(`/marketing/templates/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-2xl font-light">Templates</h3>
          <p className="text-xs text-muted-foreground">Reusable HTML blocks for broadcasts (welcome, activation, digest…).</p>
        </div>
        <Button data-testid="mkt-new-template-btn" onClick={() => { setEditing(null); setOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" /> New template
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.length === 0 && (
          <div className="col-span-full p-10 text-center bg-card border border-dashed border-border rounded-2xl">
            <div className="font-display text-lg">No templates yet</div>
            <p className="text-sm text-muted-foreground mt-1">Create one to save time on broadcasts.</p>
          </div>
        )}
        {rows.map((t) => (
          <div key={t.id} className="bg-card border border-border rounded-2xl p-4" data-testid={`mkt-template-${t.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-display text-lg truncate">{t.name}</div>
                <div className="text-xs text-muted-foreground truncate">Subject: {t.subject}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditing(t); setOpen(true); }}>
                  <Edit className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(t.id)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            {t.description && <p className="text-xs text-muted-foreground mt-2">{t.description}</p>}
            <div className="mt-3 p-2 rounded bg-muted/40 max-h-24 overflow-y-auto">
              <div className="text-[10px] font-mono truncate">{t.body_html.slice(0, 200)}…</div>
            </div>
          </div>
        ))}
      </div>

      <TemplateDialog open={open} onOpenChange={setOpen} initial={editing} onDone={() => { setOpen(false); load(); }} />
    </div>
  );
}

function TemplateDialog({ open, onOpenChange, initial, onDone }) {
  const [form, setForm] = useState({ name: "", subject: "", body_html: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!open) return;
    setErr(null);
    if (initial) setForm({ name: initial.name, subject: initial.subject, body_html: initial.body_html, description: initial.description || "" });
    else setForm({ name: "", subject: "", body_html: "", description: "" });
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault(); setErr(null); setSaving(true);
    try {
      if (initial) await api.put(`/marketing/templates/${initial.id}`, form);
      else await api.post("/marketing/templates", form);
      toast.success(initial ? "Template updated" : "Template created");
      onDone?.();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-light text-2xl">{initial ? "Edit template" : "New template"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs">Name</Label>
            <Input required data-testid="mkt-template-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs">Subject</Label>
            <Input required data-testid="mkt-template-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs">HTML body</Label>
            <Textarea required rows={12} data-testid="mkt-template-body" value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} className="mt-1.5 font-mono text-xs" />
          </div>
          <div>
            <Label className="text-xs">Description (internal)</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" />
          </div>
          {err && <div className="text-sm text-destructive">{err}</div>}
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="mkt-template-submit">{saving ? "Saving…" : (initial ? "Save" : "Create")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
